import React, { useState } from "react";
//#region types
import { AdaptiveDetailTableViewProps } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from "@mui/material";
import { useTableHelper, useValueFormatter } from "./tables";
import Icon from "./icon";
//#endregion
import { IconDefinitionConstants } from "utils/constants";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

function AdaptiveDetailTableView<RowType extends Record<string, any>>(props: AdaptiveDetailTableViewProps<RowType>) {
  const { data, properties, hideHeader, hiddenColumns, className, addRowSeparators, getRowChildren, getRowKey } = props;

  const { getFormattedValue } = useValueFormatter();
  const { getTableColumnTitle } = useTableHelper();

  const [expandedKeys, setExpandedKeys] = useState<Set<React.Key>>(() => new Set());

  const hiddenColumnsSet = new Set(hiddenColumns || []);
  const propertiesList = Object.entries(properties);
  const displayProperties = propertiesList.filter(([key]) => !hiddenColumnsSet.has(key));

  const resolveRowKey = (row: RowType, index: number): React.Key => (getRowKey ? getRowKey(row, index) : index);

  const toggleRow = (key: React.Key) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const renderCells = (
    row: RowType,
    rowIndex: number,
    opts: { isExpandable?: boolean; isExpanded?: boolean; isChild?: boolean }
  ) =>
    displayProperties.map(([key, cellProps], cellIndex) => {
      const content = getFormattedValue(cellProps?.formatter ? cellProps.formatter(row, rowIndex) : row[key]);
      const isFirstColumn = cellIndex === 0;

      return (
        <TableCell
          key={`cell-${rowIndex}-${cellIndex}`}
          className={clsx(
            cssExports.tableCell,
            isFirstColumn && opts.isChild && cssExports.adaptiveChildCell,
            cellProps?.className
          )}
          align={cellProps?.alignment}
          style={{
            width: cellProps?.width,
            maxWidth: cellProps?.maxWidth,
            ...(cellProps?.noWrap && { whiteSpace: "nowrap" }),
          }}
        >
          {isFirstColumn && (opts.isExpandable || opts.isChild) ? (
            <span className={cssExports.adaptiveFirstCell}>
              <span
                className={cssExports.adaptiveExpandChevron}
                aria-expanded={opts.isExpandable ? !!opts.isExpanded : undefined}
              >
                {opts.isExpandable && (
                  <Icon
                    icon={
                      opts.isExpanded
                        ? IconDefinitionConstants.WChevronSmallDown
                        : IconDefinitionConstants.WChevronSmallRight
                    }
                  />
                )}
              </span>
              <span>{content}</span>
            </span>
          ) : (
            content
          )}
        </TableCell>
      );
    });

  return (
    <Grid
      container
      className={clsx(
        cssExports.adaptiveDetailTableViewContainerOuter,
        addRowSeparators && cssExports.adaptiveDetailTableWithSeparators,
        className
      )}
    >
      <TableContainer classes={{ root: cssExports.adaptiveDetailTableViewRoot }}>
        <Table aria-label="dynamic detail table" className={cssExports.adaptiveDetailTable}>
          {!hideHeader && (
            <TableHead className={cssExports.tableHead}>
              <TableRow className={cssExports.tableRow}>
                {displayProperties.map(([headerKey, value], index) => (
                  <TableCell
                    key={`header-${index}`}
                    className={clsx(cssExports.tableCell, value?.className)}
                    align={value?.alignment}
                    style={{
                      width: value?.width,
                      maxWidth: value?.maxWidth,
                      ...(value?.noWrap && { whiteSpace: "nowrap" }),
                    }}
                  >
                    {value?.header && getTableColumnTitle(value.header)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody className={cssExports.tableBody}>
            {data.map((row, rowIndex) => {
              const rowKey = resolveRowKey(row, rowIndex);
              const children = getRowChildren?.(row);
              const isExpandable = !!children?.length;
              const isExpanded = isExpandable && expandedKeys.has(rowKey);

              return (
                <React.Fragment key={`row-${rowKey}`}>
                  <TableRow
                    className={clsx(cssExports.tableRow, isExpandable && cssExports.adaptiveExpandableRow)}
                    onClick={isExpandable ? () => toggleRow(rowKey) : undefined}
                  >
                    {renderCells(row, rowIndex, { isExpandable, isExpanded })}
                  </TableRow>
                  {isExpanded &&
                    children!.map((child, childIndex) => (
                      <TableRow key={`row-${rowKey}-child-${childIndex}`} className={cssExports.tableRow}>
                        {renderCells(child, childIndex, { isChild: true })}
                      </TableRow>
                    ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}

export default AdaptiveDetailTableView;
